/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */
import {
	ADDRESS_CLAMP_TO_EDGE,
	AppBase,
	CameraComponent,
	Color,
	Entity,
	FILTER_LINEAR,
	Layer,
	ModelComponent,
	PIXELFORMAT_R8_G8_B8_A8,
	PostEffect,
	RenderComponent,
	RenderTarget,
	ScriptType,
	Shader,
	Texture,
	Vec4,
} from "playcanvas";
import { createOutlineShader } from "./shaders/outline";
import { createQuadShader } from "./shaders/quad-shader";

export interface Outline {
	color: Color;
	interiorColor: Color;
	backgroundColor: Color;
	useBackgroundColor: boolean;
	thickness: number;
	layerName: string;
	cameraEntity: Entity | null;
}

export interface OutlineAttributes {
	color: number[];
	interiorColor: number[];
	backgroundColor: number[];
	useBackgroundColor: boolean;
	thickness: number;
	layerName: string;
	cameraEntity: Entity | null;
}

export class Outline extends ScriptType {
	private _effect!: OutlineEffect;
	private _shaderPass!: number;
	private _renderTarget!: RenderTarget;
	private _outlineEntity!: Entity;
	private _outlineCamera!: CameraComponent;
	private _outlineLayer!: Layer;
	private _originalCamera!: CameraComponent;

	static register() {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(this as any).__name = "Outline";

		Outline.attributes.add("color", {
			type: "rgba",
			default: [1, 1, 1, 1],
			title: "Outline color",
		});

		Outline.attributes.add("interiorColor", {
			type: "rgba",
			default: [1, 1, 1, 0],
			title: "Interior tint color",
		});

		Outline.attributes.add("backgroundColor", {
			type: "rgba",
			default: [1, 1, 1, 0],
			title: "Background / exterior tint color",
		});

		Outline.attributes.add("useBackgroundColor", {
			type: "boolean",
			default: false,
			title: "Use background / exterior tint color",
		});

		Outline.attributes.add("thickness", {
			type: "number",
			default: 2.0,
			min: 1.0,
			max: 10,
			step: 0.1,
			title: "Outline thickness",
		});

		Outline.attributes.add("layerName", {
			type: "string",
			default: "SelectionLayer",
			title: "Outline layer name",
			description: "The layer will be created if it doesn't exist",
		});

		Outline.attributes.add("cameraEntity", {
			type: "entity",
			title: "Camera entity",
			description:
				"When not specified, the entity this script is attached to must have a camera",
		});
	}

	get shaderPass(): number {
		return this._shaderPass;
	}

	get outlineLayer(): Layer {
		return this._outlineLayer;
	}

	get texture(): Texture {
		return this._renderTarget.colorBuffer;
	}

	addToOutlineLayer(...entities: Entity[]): void {
		for (const entity of entities) {
			this._useOutlineLayerForEntity(entity, true);
		}
	}

	removeFromOutlineLayer(...entities: Entity[]): void {
		for (const entity of entities) {
			this._useOutlineLayerForEntity(entity, false);
		}
	}

	initialize(): void {
		const cameraEntity = this.cameraEntity ?? this.entity;
		const camera = cameraEntity.camera;
		if (!camera) {
			throw new Error("Outline requires a camera component");
		}
		this._originalCamera = camera;

		this._initOutlineLayer();
		this._initOutlineEntity();
		this._initOrUpdateRenderTarget();
		this._effect = new OutlineEffect(this.app, this);
		this._onEnabled(true);

		this.on("state", this._onEnabled.bind(this));
		this.on("destroy", () => {
			this._onEnabled(false);
			// Remove the owned effect before releasing its shader so a camera queue
			// can never retain a destroyed post-effect instance.
			this._outlineEntity?.destroy();
			this._renderTarget?.colorBuffer.destroy();
			this._renderTarget?.destroy();
			this._effect?.destroy();
		});
	}

	postUpdate(): void {
		this._initOrUpdateRenderTarget();
		this._updateOutlineCamera();
	}

	private _onEnabled(enabled: boolean) {
		this._outlineEntity.enabled = enabled;
		// TODO: fix bad PlayCanvas typescript declaration requiring the following workaround
		if (enabled) {
			if (
				!this._originalCamera.postEffects.effects.some(
					(entry) => entry.effect === this._effect,
				)
			) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				this._originalCamera.postEffects.addEffect(this._effect as any);
			}
		} else {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			this._originalCamera.postEffects.removeEffect(this._effect as any);
		}
	}

	private _initOutlineLayer(): void {
		let layer = this.app.scene.layers.getLayerByName(this.layerName);
		if (!layer) {
			layer = new Layer({ name: this.layerName });
			this.app.scene.layers.push(layer);
		}
		this._outlineLayer = layer;
	}

	private _initOutlineEntity(): void {
		this._outlineEntity = new Entity("Outline");
		this._outlineEntity.addComponent("camera", {
			layers: [this._outlineLayer.id],
			priority: (this._originalCamera.priority ?? 1) - 1,
			clearColor: new Color(0.0, 0.0, 0.0, 0.0),
		});
		this._outlineCamera = this._outlineEntity.camera!;
		this._shaderPass = this._outlineCamera.setShaderPass("OutlinePass");
		this.entity.addChild(this._outlineEntity);
	}

	private _initOrUpdateRenderTarget(): void {
		const device = this.app.graphicsDevice;
		const needsUpdate =
			!this._renderTarget ||
			device.width !== this._renderTarget.colorBuffer.width ||
			device.height !== this._renderTarget.colorBuffer.height;

		if (needsUpdate) {
			this._renderTarget?.colorBuffer.destroy();
			this._renderTarget?.destroy();
			this._renderTarget = new RenderTarget({
				colorBuffer: new Texture(device, {
					width: device.width,
					height: device.height,
					format: PIXELFORMAT_R8_G8_B8_A8,
					minFilter: FILTER_LINEAR,
					magFilter: FILTER_LINEAR,
					addressU: ADDRESS_CLAMP_TO_EDGE,
					addressV: ADDRESS_CLAMP_TO_EDGE,
					mipmaps: false,
				}),
				depth: true,
			});
		}
		if (this._outlineCamera.renderTarget !== this._renderTarget) {
			this._outlineCamera.renderTarget = this._renderTarget;
		}
	}

	private _updateOutlineCamera = (() => {
		const keys: (keyof CameraComponent)[] = [
			"fov",
			"orthoHeight",
			"horizontalFov",
			"projection",
			"aspectRatio",
			"nearClip",
			"farClip",
		];
		return (): void => {
			this._outlineEntity.setPosition(
				this._originalCamera.entity.getPosition(),
			);
			this._outlineEntity.setEulerAngles(
				this._originalCamera.entity.getEulerAngles(),
			);

			const originalCamera = this._originalCamera;
			const outlineCamera = this._outlineCamera;
			for (const key of keys) {
				if (outlineCamera[key] !== originalCamera[key]) {
					(outlineCamera as any)[key] = originalCamera[key]; // eslint-disable-line @typescript-eslint/no-explicit-any
				}
			}
		};
	})();

	private _useOutlineLayerForEntity(entity: Entity, useLayer: boolean): void {
		const components = [
			...entity.findComponents("model"),
			...entity.findComponents("render"),
		] as (ModelComponent | RenderComponent)[];
		const layerId = this._outlineLayer.id;

		for (const component of components) {
			const index = component.layers.indexOf(layerId);
			if (useLayer && index < 0) {
				component.layers = [...component.layers, layerId];
			} else if (!useLayer && index >= 0) {
				component.layers = [
					...component.layers.slice(0, index),
					...component.layers.slice(index + 1),
				];
			}
		}
	}
}

class OutlineEffect extends PostEffect {
	shader!: Shader;
	private _uOutlineCol = new Float32Array(4);
	private _uInteriorCol = new Float32Array(4);
	private _uBackgroundCol = new Float32Array(4);
	private _uInvTextureSize = new Float32Array(4);

	constructor(
		readonly app: AppBase,
		private _outlineScript: {
			color: Color;
			outlineLayer: Layer;
			interiorColor: Color;
			backgroundColor: Color;
			useBackgroundColor: boolean;
			texture: Texture;
			thickness: number;
		},
	) {
		super(app.graphicsDevice);
		this.updateShaders();
	}

	render(inputTarget: RenderTarget, outputTarget: RenderTarget, rect: Vec4) {
		const device = this.device;
		const scope = device.scope;
		const {
			color,
			outlineLayer,
			interiorColor,
			backgroundColor,
			useBackgroundColor,
			texture,
			thickness,
		} = this._outlineScript;

		this._uOutlineCol[0] = color.r;
		this._uOutlineCol[1] = color.g;
		this._uOutlineCol[2] = color.b;
		this._uOutlineCol[3] = color.a;

		this._uInteriorCol[0] = interiorColor.r;
		this._uInteriorCol[1] = interiorColor.g;
		this._uInteriorCol[2] = interiorColor.b;
		this._uInteriorCol[3] = interiorColor.a;

		const backgroundColorEnabled =
			useBackgroundColor && outlineLayer.meshInstances.length > 0;

		if (backgroundColorEnabled) {
			this._uBackgroundCol[0] = backgroundColor.r;
			this._uBackgroundCol[1] = backgroundColor.g;
			this._uBackgroundCol[2] = backgroundColor.b;
			this._uBackgroundCol[3] = backgroundColor.a;
		} else {
			this._uBackgroundCol[0] = 1;
			this._uBackgroundCol[1] = 1;
			this._uBackgroundCol[2] = 1;
			this._uBackgroundCol[3] = 0;
		}

		this._uInvTextureSize[0] = 1 / texture.width;
		this._uInvTextureSize[1] = 1 / texture.height;

		scope.resolve("uOutlineCol").setValue(this._uOutlineCol);
		scope.resolve("uInteriorCol").setValue(this._uInteriorCol);
		scope.resolve("uBackgroundCol").setValue(this._uBackgroundCol);
		scope.resolve("uInvTextureSize").setValue(this._uInvTextureSize);
		scope.resolve("uColorBuffer").setValue(inputTarget.colorBuffer);
		scope.resolve("uOutlineTex").setValue(texture);
		scope.resolve("uHalfThickness").setValue(thickness * 0.5);

		this.drawQuad(outputTarget, this.shader, rect);
	}

	destroy(): void {
		this.shader?.destroy();
	}

	updateShaders() {
		const kernel = this._createKernel(0, 1, 8, true);

		this.shader?.destroy();
		this.shader = createQuadShader(this.device, {
			name: "ConfiguratorOutlineEffect",
			fragment: createOutlineShader(kernel),
		});
	}

	private _createKernel(
		kernelAngle: number,
		kernelRadius: number,
		perimeterSamples: number,
		centerSample: boolean,
	): string[] {
		const sample = (angle: number) =>
			[
				Math.cos(kernelAngle + angle) * kernelRadius,
				Math.sin(kernelAngle + angle) * kernelRadius,
			]
				.map((val) => val.toFixed(6))
				.join(", ");

		const deltaAngle = (2 * Math.PI) / perimeterSamples;
		const samples: string[] = centerSample ? ["0.0, 0.0"] : [];

		for (let idx = 0; idx < perimeterSamples; idx++) {
			samples.push(sample(idx * deltaAngle));
		}
		return samples;
	}
}
